import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Building2, Users, Activity, ShieldCheck, Trash2, KeyRound, Loader2, Shield, Copy,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/lib/format";
import { CARD, Label, Loader, ErrorRetry } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label as UiLabel } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ROLE_LABEL = { admin: "Administrateur", manager: "Gestionnaire", viewer: "Lecture seule" };

export default function Admin() {
  const { user: me } = useAuth();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [delTenant, setDelTenant] = useState(null);
  const [delUser, setDelUser] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [copyTo, setCopyTo] = useState(null);
  const [copyFrom, setCopyFrom] = useState("");

  const doCopy = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/tenants/${copyTo.id}/copy-expenses`, { source_org_id: copyFrom });
      toast.success(`${data.copied} dépense(s) copiée(s)${data.skipped ? `, ${data.skipped} déjà présente(s)` : ""} · ${data.recurring_copied} récurrence(s) · ${data.categories_created} catégorie(s) créée(s)`);
      setCopyTo(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [s, t, u] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/tenants"),
        api.get("/admin/users"),
      ]);
      setStats(s.data); setTenants(t.data); setUsers(u.data);
    } catch (e) { setError(true); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doDeleteTenant = async () => {
    try { await api.delete(`/admin/tenants/${delTenant.id}`); toast.success("Cabinet supprimé"); setDelTenant(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doDeleteUser = async () => {
    try { await api.delete(`/admin/users/${delUser.id}`); toast.success("Utilisateur supprimé"); setDelUser(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doResetPw = async () => {
    if (pw.length < 6) { toast.error("Minimum 6 caractères"); return; }
    setSaving(true);
    try { await api.post(`/admin/users/${pwUser.id}/reset-password`, { password: pw }); toast.success(`Mot de passe réinitialisé pour ${pwUser.email}`); setPwUser(null); setPw(""); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const setRole = async (u, role) => {
    try { await api.post(`/admin/users/${u.id}/role`, { role }); toast.success("Rôle mis à jour"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleActive = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { active: u.active === false }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleSuper = async (u) => {
    try { await api.patch(`/admin/users/${u.id}`, { is_superadmin: !u.is_superadmin }); toast.success("Statut super-admin mis à jour"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <Loader />;
  if (error) return <ErrorRetry onRetry={load} />;

  const STATS = [
    { icon: Building2, label: "Cabinets", value: stats.tenants },
    { icon: Users, label: "Utilisateurs", value: stats.users },
    { icon: Activity, label: "Mouvements", value: stats.transactions },
    { icon: ShieldCheck, label: "Super-admins", value: stats.superadmins },
  ];

  return (
    <div>
      <div className="flex items-center gap-2">
        <Shield className="h-6 w-6 text-[#065F46]" />
        <div>
          <p className="text-sm text-gray-400">Console de plateforme</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Administration</h1>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {STATS.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className={`${CARD} p-5`} data-testid={`admin-stat-${s.label}`}>
              <div className="flex items-center gap-2 text-gray-400"><Icon className="h-4 w-4" /><Label>{s.label}</Label></div>
              <p className="mt-2 font-display text-3xl font-black">{s.value}</p>
            </div>
          );
        })}
      </div>

      <Tabs defaultValue="tenants" className="mt-6">
        <TabsList className="rounded-full bg-gray-100 p-1">
          <TabsTrigger value="tenants" className="rounded-full" data-testid="admin-tab-tenants">Cabinets</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full" data-testid="admin-tab-users">Utilisateurs</TabsTrigger>
        </TabsList>

        <TabsContent value="tenants" className="mt-5">
          <div className={`${CARD} p-4 sm:p-6`}>
            {tenants.map((t) => (
              <div key={t.id} className="flex items-center gap-3 border-b border-gray-50 py-3 last:border-0" data-testid={`admin-tenant-${t.id}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-50 text-[#065F46]"><Building2 className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {t.name} {t.is_mine && <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Votre cabinet</span>}
                  </p>
                  <p className="truncate text-xs text-gray-400">{t.owner_email || "—"} · {t.user_count} utilisateur(s) · {t.transaction_count} mouvement(s) · créé le {fmtDate(t.created_at)}</p>
                </div>
                <button onClick={() => { setCopyTo(t); setCopyFrom(""); }} data-testid={`admin-copy-expenses-${t.id}`} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-[#065F46] hover:bg-emerald-50" title="Copier les dépenses d'un autre cabinet vers celui-ci">
                  <Copy className="h-4 w-4" /><span className="hidden sm:inline">Copier les dépenses vers…</span>
                </button>
                <button
                  disabled={t.is_mine}
                  onClick={() => setDelTenant(t)}
                  data-testid={`admin-delete-tenant-${t.id}`}
                  className="rounded-lg p-2 text-gray-400 enabled:hover:bg-rose-50 enabled:hover:text-rose-600 disabled:opacity-30"
                  title={t.is_mine ? "Impossible de supprimer votre propre cabinet" : "Supprimer ce cabinet"}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-5">
          <div className={`${CARD} p-4 sm:p-6`}>
            {users.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-3 border-b border-gray-50 py-3 last:border-0" data-testid={`admin-user-${u.id}`}>
                <span className={`flex h-10 w-10 items-center justify-center rounded-full font-bold ${u.active === false ? "bg-gray-100 text-gray-400" : "bg-emerald-50 text-[#065F46]"}`}>
                  {(u.name || u.email).charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">
                    {u.name || u.email}
                    {u.is_superadmin && <span className="ml-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">Super-admin</span>}
                    {u.active === false && <span className="ml-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">Désactivé</span>}
                  </p>
                  <p className="truncate text-xs text-gray-400">{u.email} · {u.organization_name || "—"} · {u.auth_provider === "google" ? "Google" : "Mot de passe"}</p>
                </div>
                <Select value={u.role} onValueChange={(v) => setRole(u, v)}>
                  <SelectTrigger className="h-9 w-40 rounded-full text-xs" data-testid={`admin-role-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1.5" title="Actif">
                  <Switch checked={u.active !== false} onCheckedChange={() => toggleActive(u)} data-testid={`admin-active-${u.id}`} />
                </div>
                <button onClick={() => { setPwUser(u); setPw(""); }} data-testid={`admin-resetpw-${u.id}`} className="rounded-lg p-2 text-gray-400 hover:bg-amber-50 hover:text-amber-600" title="Réinitialiser le mot de passe">
                  <KeyRound className="h-4 w-4" />
                </button>
                <button onClick={() => toggleSuper(u)} data-testid={`admin-super-${u.id}`} className={`rounded-lg p-2 hover:bg-violet-50 ${u.is_superadmin ? "text-violet-600" : "text-gray-300"}`} title="Basculer super-admin">
                  <ShieldCheck className="h-4 w-4" />
                </button>
                <button
                  disabled={u.id === me?.id}
                  onClick={() => setDelUser(u)}
                  data-testid={`admin-delete-user-${u.id}`}
                  className="rounded-lg p-2 text-gray-400 enabled:hover:bg-rose-50 enabled:hover:text-rose-600 disabled:opacity-30"
                  title={u.id === me?.id ? "Impossible de vous supprimer" : "Supprimer l'utilisateur"}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Reset password dialog */}
      <Dialog open={!!pwUser} onOpenChange={(v) => !v && setPwUser(null)}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="admin-pw-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Réinitialiser le mot de passe</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-500">Nouveau mot de passe pour <b>{pwUser?.email}</b>.</p>
            <div>
              <UiLabel>Nouveau mot de passe</UiLabel>
              <Input data-testid="admin-pw-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1.5 rounded-xl" placeholder="Minimum 6 caractères" />
            </div>
            <Button data-testid="admin-pw-submit" onClick={doResetPw} disabled={saving} className="w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Réinitialiser"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!copyTo} onOpenChange={(v) => !v && setCopyTo(null)}>
        <DialogContent className="rounded-3xl sm:max-w-md" data-testid="admin-copy-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Copier les dépenses</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-gray-500">Vers <b>{copyTo?.name}</b>. Toutes les dépenses (payées et à venir) et leurs récurrences sont copiées, sans site. Les catégories manquantes sont créées. Une dépense déjà copiée n'est pas dupliquée.</p>
            <div>
              <UiLabel>Cabinet de départ</UiLabel>
              <Select value={copyFrom} onValueChange={setCopyFrom}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="admin-copy-source"><SelectValue placeholder="Choisir le cabinet" /></SelectTrigger>
                <SelectContent>
                  {tenants.filter((t) => t.id !== copyTo?.id).map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.owner_email || "—"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="admin-copy-submit" onClick={doCopy} disabled={saving || !copyFrom} className="w-full rounded-full bg-[#065F46] py-6 hover:bg-[#047857]">
              {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Copier les dépenses"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete tenant confirm */}
      <AlertDialog open={!!delTenant} onOpenChange={(v) => !v && setDelTenant(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le cabinet « {delTenant?.name} » ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Tous les utilisateurs, mouvements, catégories et paramètres de ce cabinet seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteTenant} data-testid="admin-confirm-delete-tenant" className="rounded-full bg-rose-600 hover:bg-rose-700">Supprimer définitivement</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete user confirm */}
      <AlertDialog open={!!delUser} onOpenChange={(v) => !v && setDelUser(null)}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'utilisateur {delUser?.email} ?</AlertDialogTitle>
            <AlertDialogDescription>Son accès sera immédiatement révoqué.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteUser} data-testid="admin-confirm-delete-user" className="rounded-full bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
